import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { API, authHeaders } from "@/lib/adminApi";
import CheckoutLinkResult from "@/components/admin/CheckoutLinkResult";

/* ── New Member Slide-over ── */
const EMPTY_CHILD = { first_name: "", last_name: "", birth_date: "" };

const EMPTY_FORM = {
  first_name: "", last_name: "", email: "", phone: "",
  gender: "M", birth_date: "", birth_place: "",
  address: "", address_postal_code: "",
  civil_status: "", profession: "",
  country: "France", membership_type: "individuel", amount: "",
  payment_method: "virement", newsletter_opt_in: true,
  has_children_under_18: false, children: [],
};

const NewMemberPanel = ({ year, onClose, onSuccess }) => {
  const [form, setForm] = useState({ ...EMPTY_FORM, year });
  const [loading, setLoading] = useState(false);
  const [checkoutLink, setCheckoutLink] = useState(null);

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const setHasChildren = (value) => {
    setForm((f) => ({
      ...f,
      has_children_under_18: value,
      children: value ? (f.children.length ? f.children : [{ ...EMPTY_CHILD }]) : [],
    }));
  };

  const addChild = () => {
    setForm((f) => ({ ...f, children: [...f.children, { ...EMPTY_CHILD }] }));
  };

  const removeChild = (index) => {
    setForm((f) => ({ ...f, children: f.children.filter((_, i) => i !== index) }));
  };

  const updateChild = (index, key) => (e) => {
    const value = e.target.value;
    setForm((f) => ({
      ...f,
      children: f.children.map((c, i) => (i === index ? { ...c, [key]: value } : c)),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const children = form.children
        .filter((c) => c.first_name.trim() || c.last_name.trim())
        .map((c) => ({ ...c, birth_date: c.birth_date || null }));

      const memberRes = await axios.post(
        `${API}/members/admin-register`,
        { ...form, amount: parseFloat(form.amount) || 0, children, has_children_under_18: children.length > 0 },
        { headers: authHeaders() }
      );

      if (form.payment_method === "stripe_link") {
        const linkRes = await axios.post(
          `${API}/payments/stripe/generate-checkout-link`,
          { member_id: memberRes.data.id },
          { headers: authHeaders() }
        );
        setCheckoutLink({
          url: linkRes.data.link_url,
          expires_at: linkRes.data.expires_at,
          amount: linkRes.data.amount,
          member_name: `${form.first_name} ${form.last_name}`,
        });
        onSuccess();
      } else {
        toast.success(`${form.first_name} ${form.last_name} enregistré avec succès.`);
        onSuccess();
        onClose();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-xl bg-white shadow-2xl flex flex-col overflow-hidden" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        {/* Panel header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100" style={{ backgroundColor: "#111827" }}>
          <div>
            <h2 className="text-xl" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500, color: "#F9F6F0" }}>
              Nouveau membre
            </h2>
            <p className="text-xs mt-0.5" style={{ color: "#6B7280" }}>Enregistrement direct · paiement confirmé</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg transition-colors hover:bg-white/10">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: "#6B7280" }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form id="new-member-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          <FormSection title="Identité">
            <div className="grid grid-cols-2 gap-4">
              <PanelField label="Prénom *" value={form.first_name} onChange={set("first_name")} required />
              <PanelField label="Nom *" value={form.last_name} onChange={set("last_name")} required />
              <PanelField label="Téléphone *" value={form.phone} onChange={set("phone")} required placeholder="+33 6…" />
              <PanelField label="Email" type="email" value={form.email} onChange={set("email")} />
              <div className="space-y-1.5">
                <label className={labelCls}>Genre *</label>
                <select value={form.gender} onChange={set("gender")} required className={selectCls}>
                  <option value="M">Masculin</option>
                  <option value="F">Féminin</option>
                </select>
              </div>
              <PanelField label="Date de naissance" type="date" value={form.birth_date} onChange={set("birth_date")} />
              <PanelField label="Lieu de naissance" value={form.birth_place} onChange={set("birth_place")} />
              <div className="space-y-1.5">
                <label className={labelCls}>Situation</label>
                <select value={form.civil_status} onChange={set("civil_status")} className={selectCls}>
                  <option value="">—</option>
                  <option value="celibataire">Célibataire</option>
                  <option value="marie">Marié(e)</option>
                  <option value="divorce">Divorcé(e)</option>
                  <option value="veuf">Veuf / Veuve</option>
                </select>
              </div>
              <PanelField label="Profession" value={form.profession} onChange={set("profession")} />
            </div>
          </FormSection>

          <FormSection title="Adresse">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <PanelField label="Adresse" value={form.address} onChange={set("address")} />
              </div>
              <PanelField label="Code postal" value={form.address_postal_code} onChange={set("address_postal_code")} placeholder="75001" />
              <PanelField label="Pays" value={form.country} onChange={set("country")} />
            </div>
          </FormSection>

          <FormSection title="Enfants">
            <div className="space-y-2">
              <label className={labelCls}>Enfants à charge (-18 ans) ?</label>
              <div className="grid grid-cols-2 gap-3">
                {[{ value: true, label: "Oui" }, { value: false, label: "Non" }].map((opt) => (
                  <label
                    key={String(opt.value)}
                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border cursor-pointer transition-all ${
                      form.has_children_under_18 === opt.value
                        ? "border-emerald-500 bg-emerald-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="has_children_under_18"
                      checked={form.has_children_under_18 === opt.value}
                      onChange={() => setHasChildren(opt.value)}
                      className="accent-emerald-600"
                    />
                    <span className="text-sm text-slate-700">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {form.has_children_under_18 && (
              <div className="mt-4 space-y-3">
                {form.children.map((child, index) => (
                  <div key={index} className="rounded-xl bg-slate-50 border border-slate-200 p-4 relative">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Enfant {index + 1}</p>
                      <button
                        type="button"
                        onClick={() => removeChild(index)}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <PanelField label="Prénom" value={child.first_name} onChange={updateChild(index, "first_name")} />
                      <PanelField label="Nom" value={child.last_name} onChange={updateChild(index, "last_name")} />
                      <PanelField label="Date de naissance" type="date" value={child.birth_date} onChange={updateChild(index, "birth_date")} />
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addChild}
                  className="flex items-center gap-2 text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors"
                >
                  <Plus className="w-4 h-4" /> Ajouter un enfant
                </button>
              </div>
            )}
          </FormSection>

          <FormSection title="Adhésion & Paiement">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className={labelCls}>Type *</label>
                <select value={form.membership_type} onChange={set("membership_type")} required className={selectCls}>
                  <option value="individuel">Individuel</option>
                  <option value="famille">Famille</option>
                  <option value="etudiant">Étudiant</option>
                </select>
              </div>
              <PanelField label="Montant (€) *" type="number" value={form.amount} onChange={set("amount")} required placeholder="50" />
              <div className="col-span-2 space-y-2">
                <label className={labelCls}>Mode de paiement *</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: "virement", label: "Virement" },
                    { value: "especes", label: "Espèces" },
                    { value: "stripe_link", label: "💳 Lien Stripe" },
                  ].map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                        form.payment_method === opt.value
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <input
                        type="radio"
                        name="payment_method"
                        value={opt.value}
                        checked={form.payment_method === opt.value}
                        onChange={set("payment_method")}
                        className="accent-emerald-600"
                      />
                      <span className="text-sm text-slate-700">{opt.label}</span>
                    </label>
                  ))}
                </div>
                {form.payment_method === "stripe_link" && (
                  <p className="text-xs text-slate-400 mt-1">
                    Un lien de paiement sécurisé sera généré après l'enregistrement. L'adhérent y choisit
                    de payer l'intégralité ou un acompte suivi de mensualités. Valable 7 jours, régénérable.
                  </p>
                )}
              </div>
            </div>
          </FormSection>
        </form>

        {checkoutLink ? (
          <div className="px-6 py-5 border-t border-slate-100 bg-white space-y-3">
            <CheckoutLinkResult
              title={`${checkoutLink.member_name} enregistré — lien de paiement`}
              memberName={checkoutLink.member_name}
              url={checkoutLink.url}
              amount={checkoutLink.amount}
              expiresAt={checkoutLink.expires_at}
            />
            <button type="button" onClick={onClose}
              className="w-full text-xs text-slate-400 hover:text-slate-600 transition-colors py-1">
              Fermer
            </button>
          </div>
        ) : (
          <div className="px-6 py-4 border-t border-slate-100 flex gap-3 justify-end bg-white">
            <button type="button" onClick={onClose}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm hover:bg-slate-50 transition-all">
              Annuler
            </button>
            <button type="submit" form="new-member-form" disabled={loading}
              className="px-6 py-2.5 rounded-xl text-white text-sm font-semibold hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50"
              style={{ backgroundColor: "#059669" }}>
              {loading ? "Enregistrement…" : form.payment_method === "stripe_link" ? "Enregistrer & générer le lien" : "Enregistrer le membre"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export const labelCls = "block text-xs font-medium text-slate-500 uppercase tracking-wide";
export const selectCls = "w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all";

export const FormSection = ({ title, children }) => (
  <section>
    <div className="flex items-center gap-3 mb-4">
      <h3 className="text-xs font-bold tracking-widest text-slate-400 uppercase">{title}</h3>
      <div className="flex-1 h-px bg-slate-100" />
    </div>
    {children}
  </section>
);

export const PanelField = ({ label, type = "text", value, onChange, required, placeholder }) => (
  <div className="space-y-1.5">
    <label className={labelCls}>{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
    />
  </div>
);

export default NewMemberPanel;
